// src/components/TransitionOverlay.tsx
import React, { useEffect } from 'react';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';
import { gsap } from 'gsap';
import logoImage from "../assets/logo-partouche-multiverse.png"
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  width: 100vw;
  height: 100vh;
  display: flex;
  flex-direction: column;
  z-index: 9999;
  pointer-events: none;
`;

const Row = styled.div`
  flex: 1;
  display: flex;
`;

// add a bgColor prop to control the color dynamically
const Block = styled.div<{ origin: 'top' | 'bottom'; bgColor: string }>`
  flex: 1;
  background-color: ${({ bgColor }) => bgColor};
  transform: scaleY(1);
  transform-origin: ${({ origin }) => origin};
  will-change: transform, visibility;
  visibility: visible;
`;

const ease = 'power4.inOut';

const CenteredLogo = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 10000;
  pointer-events: none;
  opacity: 1;
  
  img {
    max-width: 300px;
    height: auto;
  }
`;

async function revealIn() {
  await new Promise<void>((resolve) => {
    gsap.set('.cg-block', { scaleY: 1, visibility: 'visible' });
    // Reset the logo to be visible before animating
    gsap.set('.cg-logo', { visibility: 'visible', opacity: 1 });
    gsap.to('.cg-block', {
      scaleY: 0,
      duration: 1,
      stagger: { each: 0.1, axis: 'x', from: 'start' },
      ease,
      onComplete: () => resolve(),
    });
    // Fade out the logo as well
    gsap.to('.cg-logo', {
      opacity: 0,
      duration: 0.5,
      ease,
    });
  });
  gsap.set('.cg-block', { visibility: 'hidden' });
  gsap.set('.cg-logo', { visibility: 'hidden' });
}

export function animateOut() {
  return new Promise<void>((resolve) => {
    gsap.set('.cg-block', { visibility: 'visible', scaleY: 0 });
    gsap.set('.cg-logo', { visibility: 'visible', opacity: 0 });
    gsap.to('.cg-block', {
      scaleY: 1,
      duration: 1,
      stagger: { each: 0.1, grid: [2, 5], axis: 'x', from: 'start' },
      ease,
      onComplete: () => resolve(),
    });
    // Fade in the logo as well
    gsap.to('.cg-logo', {
      opacity: 1,
      duration: 1,
      ease,
    });
  });
}

const TransitionOverlay: React.FC = () => {
  const location = useLocation();

  const bgColor = location.pathname === '/' ? 'black' : '#751015';

  useEffect(() => {
    revealIn().then(() =>
      window.dispatchEvent(new Event('pageTransitionFinished'))
    );
  }, [location.key]);

  return (
    <Overlay aria-hidden="true">
      <CenteredLogo className="cg-logo">
        <img style={{ scale: 0.7 }} src={logoImage} alt="Partouche Multiverse" />
      </CenteredLogo>
      <Row>
        {Array.from({ length: 5 }).map((_, i) => (
          <Block
            className="cg-block"
            origin="top"
            bgColor={bgColor}
            key={`top-${i}`}
          />
        ))}
      </Row>
      <Row>
        {Array.from({ length: 5 }).map((_, i) => (
          <Block
            className="cg-block"
            origin="bottom"
            bgColor={bgColor}
            key={`bottom-${i}`}
          />
        ))}
      </Row>
    </Overlay>
  );
};

export default TransitionOverlay;
